import { Phone, MapPin, CheckCircle, Clock, ArrowRight, Train } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const StratfordPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Stratford | Vehicle Transport E15, E20 | 24/7 Breakdown Stratford"
        description="Fast car recovery in Stratford E15, E20. 30-minute response. 24/7 breakdown service near Westfield, Olympic Park. Call 07492 639223 for car recovery Stratford."
        keywords="car recovery stratford, breakdown service stratford, car recovery e15, car recovery e20, stratford vehicle transport, westfield car recovery, olympic park breakdown"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-cyan-800 to-teal-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-cyan-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚗 Covering Stratford & Olympic Park
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Stratford
            </h1>
            <p className="text-xl text-cyan-100 mb-8">
              Fast 30-minute car recovery across Stratford E15, E20. Available 24/7 for breakdowns near Westfield, Olympic Park, Stratford Station, and all surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-cyan-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-cyan-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-600">24/7</p>
              <p className="text-sm text-slate-600">Always Open</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-600">Local</p>
              <p className="text-sm text-slate-600">East London Based</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-cyan-600">E15, E20</p>
              <p className="text-sm text-slate-600">Full Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Car Recovery Services in Stratford
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides the fastest car recovery and vehicle transport service in Stratford and surrounding areas. Whether you've broken down near Westfield Shopping Centre, Olympic Park, or Stratford International Station, our local team reaches you within 30 minutes on average across E15 and E20 postcodes.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Stratford Areas We Serve
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Stratford E15",
                "Stratford E20",
                "Westfield Stratford City",
                "Olympic Park",
                "Stratford Station",
                "Stratford International",
                "Maryland",
                "Forest Gate",
                "West Ham",
                "Stratford New Town",
                "Stratford Marsh",
                "Abbey Road"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Complete Recovery Services in Stratford
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "24/7 emergency breakdown recovery",
                "Vehicle transportation service",
                "Accident and collision recovery",
                "Jump start and battery boost",
                "Westfield car park recovery",
                "Station pickup service",
                "Roadside assistance",
                "Flat tyre recovery",
                "Out of fuel delivery",
                "Non-running vehicle transport",
                "Locked out assistance",
                "Olympic Park area recovery"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Us for Stratford Car Recovery?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Train className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Stratford Specialists</h4>
                      <p className="text-slate-600">
                        We know Stratford's complex road network - from the busy A12 to Westfield car parks. Our drivers navigate the area daily and know the fastest routes and best access points.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Fast Local Response</h4>
                      <p className="text-slate-600">
                        Based in East London, we reach most Stratford locations within 30 minutes. Faster than national services because we're already in the area.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">24/7 Available - Fixed Pricing</h4>
                      <p className="text-slate-600">
                        Available every day including weekends and bank holidays. Transparent pricing with no hidden fees or night surcharges.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Common Breakdown Locations We Cover
            </h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-8">
              <li><strong>Westfield Car Parks:</strong> Multi-storey and surface car park recovery</li>
              <li><strong>Stratford Station:</strong> Drop-off/pickup area recovery</li>
              <li><strong>Great Eastern Road (A118):</strong> Major route - priority response</li>
              <li><strong>Olympic Park roads:</strong> Event area access and recovery</li>
              <li><strong>Stratford High Street:</strong> Busy shopping area</li>
              <li><strong>A12 slip roads:</strong> Fast motorway junction recovery</li>
              <li><strong>Residential streets E15/E20:</strong> Home and work addresses</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-cyan-900 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Broken Down in Stratford? Call Us Now
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            24/7 car recovery and vehicle transport service across Stratford E15, E20 and Olympic Park area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-cyan-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-cyan-900 text-lg px-8 py-6">
                Get Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
